import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';

/**
 * Guest-menu promotions (ADR-0023). Display content only: a promotion never changes a price;
 * discounts belong to billing (E1.6c).
 */
export const promotionSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  badge: z.string().nullable(),
  image_url: z.string().nullable(),
  menu_item_id: uuidSchema.nullable(),
  starts_at: isoTimestampSchema.nullable(),
  ends_at: isoTimestampSchema.nullable(),
  active: z.boolean(),
  sort_order: z.number().int(),
  version: z.number().int(),
});
export type Promotion = z.infer<typeof promotionSchema>;

export const promotionListResponseSchema = z.object({ items: z.array(promotionSchema) });

const httpsUrlSchema = z.url({ protocol: /^https$/ }).max(1000);

/*
 * Update schemas are built from field shapes WITHOUT `.default()`: in zod 4 `.partial()` keeps
 * defaults, which would silently reset omitted fields on PATCH.
 */
const promotionFields = {
  title: z.string().trim().min(1).max(80),
  description: z.string().trim().max(300).nullable(),
  badge: z.string().trim().min(1).max(24).nullable(),
  image_url: httpsUrlSchema.nullable(),
  menu_item_id: uuidSchema.nullable(),
  starts_at: isoTimestampSchema.nullable(),
  ends_at: isoTimestampSchema.nullable(),
  sort_order: z.number().int().min(0),
};

const windowIsOrdered = (v: { starts_at?: string | null; ends_at?: string | null }) =>
  !v.starts_at || !v.ends_at || v.ends_at > v.starts_at;
const windowIssue = { message: 'The end must be after the start.', path: ['ends_at'] };

export const createPromotionRequestSchema = z
  .object({
    ...promotionFields,
    description: promotionFields.description.default(null),
    badge: promotionFields.badge.default(null),
    image_url: promotionFields.image_url.default(null),
    menu_item_id: promotionFields.menu_item_id.default(null),
    starts_at: promotionFields.starts_at.default(null),
    ends_at: promotionFields.ends_at.default(null),
    sort_order: promotionFields.sort_order.default(0),
  })
  .refine(windowIsOrdered, windowIssue);

export const updatePromotionRequestSchema = z
  .object({ ...promotionFields, active: z.boolean() })
  .partial()
  .extend({ expected_version: z.number().int().nonnegative() })
  .refine(windowIsOrdered, windowIssue);
